import React, { useMemo } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../annuaire/designSystem';
import {
  dateCourte,
  ecartJours,
  heure,
  jourEtiquette,
  jourLong,
  majuscule,
  parJour,
  plusTot,
} from '../../annuaire/tunnel/creneaux';
import { icone } from '../../annuaire/tunnel/ui';

/**
 * La carte de rendez-vous de la fiche d'un atelier Fixer Lite (lot 2,
 * direction validée par Clément le 22/09 :
 * `docs/designs/fiche-annuaire-rdv-lots23-liste.md`).
 *
 * Ordinateur (« colonne compacte ») : une carte étroite et collante à droite,
 * « Réparation à domicile » en tête, le prochain horaire, le bouton, puis
 * « Appeler le réparateur ». Mobile (« barre sous la tête ») : un bloc teinté sous
 * la tête de fiche, avec trois horaires cliquables à la place du prochain
 * horaire. Un clic sur un horaire ouvre le tunnel avec ce créneau déjà
 * choisi : l'heure en réservation instantanée, le jour en demande.
 *
 * `rdv` : le mode et les horaires, chargés par `useRdvAtelier`.
 */

const LARGE = '992px';
const TEINTE = { instant: '#f6fdd9', demande: '#f3f8ff' };
const BORD = { instant: colors.vert, demande: '#cfe2fc' };

const Carte = styled.div`
  display: grid;
  gap: 14px;
  padding: 18px 22px;
  border-radius: 20px;
  background: ${(p) => TEINTE[p.$mode]};
  font-family: ${fonts.body};
  color: ${colors.textDark};

  @media (min-width: ${LARGE}) {
    gap: 12px;
    padding: 0 18px 18px;
    border: 2px solid ${(p) => BORD[p.$mode]};
    background: ${colors.white};
    overflow: hidden;
  }
`;

const Domicile = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  line-height: 1.3;
  color: ${colors.textSecondary};

  img {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    object-fit: contain;
  }

  b {
    display: block;
    font-size: 15.5px;
    font-weight: 600;
    color: ${colors.textDark};
  }

  @media (min-width: ${LARGE}) {
    margin: 0 -18px;
    padding: 14px 18px;
    background: ${(p) => TEINTE[p.$mode]};
  }
`;

const Creneaux = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (min-width: ${LARGE}) {
    display: none;
  }
`;

const Creneau = styled.button`
  min-height: 48px;
  padding: 6px 4px;
  border: 1.5px solid ${colors.bleu};
  border-radius: 12px;
  background: ${colors.white};
  color: ${colors.bleu};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  cursor: pointer;

  small {
    display: block;
    font-weight: 300;
    font-size: 11.5px;
    color: ${colors.textSecondary};
  }
`;

const Gris = styled.span`
  display: block;
  height: 48px;
  border-radius: 12px;
  background: rgba(49, 73, 89, 0.08);
`;

const Prochain = styled.p`
  display: none;
  margin: 0;
  font-size: 13.5px;
  line-height: 1.4;
  color: ${colors.textSecondary};

  b {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.textDark};
  }

  @media (min-width: ${LARGE}) {
    display: block;
  }
`;

const GrisLigne = styled.span`
  display: block;
  width: 120px;
  height: 18px;
  margin-top: 3px;
  border-radius: 6px;
  background: rgba(49, 73, 89, 0.08);
`;

const Vide = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${colors.textDark};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (min-width: ${LARGE}) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const Bouton = styled.button`
  flex: 1;
  min-height: 48px;
  padding: 0 20px;
  border: none;
  border-radius: 14px;
  background: ${colors.vert};
  color: ${colors.bleuFonce};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;
  cursor: pointer;

  @media (min-width: ${LARGE}) {
    flex: none;
  }
`;

// FIXERLIGHT-049 — « Écrire au réparateur » : un second CTA, secondaire
// (bordure, pas de fond plein), sous les actions principales. Toujours
// visible quand un compte Fixer Lite existe, mobile et ordinateur — pas de
// media query dédiée comme `Appeler`, la fiche annuaire n'est pas contrainte
// en largeur ici.
const Ecrire = styled.button`
  width: 100%;
  min-height: 44px;
  margin-top: 10px;
  padding: 0 16px;
  border: 1.5px solid ${colors.bleu};
  border-radius: 14px;
  background: ${colors.white};
  color: ${colors.bleu};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
`;

const Appeler = styled.a`
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  text-align: center;
  color: ${colors.bleuFonce};
  text-decoration: none;

  &:hover,
  &:focus {
    color: ${colors.bleuFonce};
  }

  /* « Appeler » à côté du bouton sur mobile, « Appeler le réparateur » dessous
     sur ordinateur, comme les maquettes validées. */
  span {
    display: none;
  }

  @media (min-width: ${LARGE}) {
    span {
      display: inline;
    }
  }
`;

const NB_CRENEAUX = 3;

/** « Demain », sinon « Jeudi 25 septembre ». */
const jour = (iso) =>
  ecartJours(iso) < 2 ? jourEtiquette(iso) : majuscule(jourLong(iso));

export default function CarteRendezVous({
  rdv,
  onPrendreRendezVous,
  onEcrireAuReparateur,
}) {
  const { instant, tel, dispo } = rdv;
  const mode = instant ? 'instant' : 'demande';

  const choix = useMemo(() => {
    if (instant) {
      return [...dispo.slots]
        .sort((a, b) => a.localeCompare(b))
        .slice(0, NB_CRENEAUX)
        .map((iso) => ({
          cle: iso,
          haut: heure(iso),
          bas: jourEtiquette(iso),
          label: `${heure(iso)} ${jourEtiquette(iso)}`,
          preselection: { heure: iso },
        }));
    }
    return parJour(dispo.slots)
      .slice(0, NB_CRENEAUX)
      .map((j) => ({
        cle: j.cle,
        haut: jourEtiquette(j.creneaux[0]),
        bas: dateCourte(j.creneaux[0]),
        label: `${jourEtiquette(j.creneaux[0])} ${dateCourte(j.creneaux[0])}`,
        preselection: { jour: j.cle },
      }));
  }, [dispo.slots, instant]);

  const premier = plusTot(dispo.slots);

  let horaires = null;
  if (dispo.etat === 'charge') {
    horaires = (
      <>
        <Prochain aria-hidden="true">
          Prochain horaire
          <GrisLigne />
        </Prochain>
        <Creneaux aria-hidden="true">
          <Gris />
          <Gris />
          <Gris />
        </Creneaux>
      </>
    );
  } else if (dispo.etat === 'ok' && !premier) {
    horaires = <Vide>Aucun horaire libre pour l’instant.</Vide>;
  } else if (dispo.etat === 'ok') {
    horaires = (
      <>
        <Prochain>
          {instant ? 'Prochain horaire' : 'Prochain jour disponible'}
          <b>
            {instant ? `${jour(premier)}, ${heure(premier)}` : jour(premier)}
          </b>
        </Prochain>
        <Creneaux>
          {choix.map((c) => (
            <Creneau
              key={c.cle}
              type="button"
              // Le nom accessible commence par le texte visible (commande
              // vocale : « 10:00 Demain »).
              aria-label={`${c.label}, prendre rendez-vous`}
              onClick={() => onPrendreRendezVous(c.preselection)}>
              {c.haut}
              <small>{c.bas}</small>
            </Creneau>
          ))}
        </Creneaux>
      </>
    );
  }

  return (
    <Carte $mode={mode}>
      <Domicile $mode={mode}>
        <img src={icone(instant ? 'ENERGIE' : 'CALENDRIER')} alt="" />
        <div>
          <b>Réparation à domicile</b>
          Le réparateur vient chez vous ·{' '}
          {instant ? 'confirmée instantanément' : 'confirmée par email'}
        </div>
      </Domicile>
      {horaires}
      <Actions>
        <Bouton type="button" onClick={() => onPrendreRendezVous(null)}>
          Prendre rendez-vous
        </Bouton>
        {tel && (
          <Appeler href={`tel:${tel}`} aria-label="Appeler le réparateur">
            Appeler<span> le réparateur</span>
          </Appeler>
        )}
      </Actions>
      {onEcrireAuReparateur && (
        <Ecrire
          type="button"
          onClick={onEcrireAuReparateur}
          data-testid="write-to-fixer-button">
          Écrire au réparateur
        </Ecrire>
      )}
    </Carte>
  );
}
